// JWT Token Debugging Utility
// Integrated into frontend for easier authentication troubleshooting

import { fetchAuthSession } from 'aws-amplify/auth';

export interface JWTDebugResult {
  token?: string;
  isValid: boolean;
  isExpired: boolean;
  error?: string;
  claims?: any;
  header?: any;
  authHeader?: string;
  analysis: {
    tokenLength?: number;
    hasProblematicChars: boolean;
    partsCount?: number;
    timeUntilExpiry?: number;
  };
}

export class JWTDebugger {
  static async analyzeToken(): Promise<JWTDebugResult> {
    console.log('🔍 JWT Token Debug Analysis');
    console.log('============================');
    
    try {
      // Get the current session from AWS Amplify
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        return {
          isValid: false,
          isExpired: true,
          error: 'No authentication token available',
          analysis: { hasProblematicChars: false }
        };
      }

      console.log('📊 Token Basic Info:');
      console.log('- Token length:', token.length);
      console.log('- Token type:', typeof token);
      console.log('- First 50 chars:', token.substring(0, 50));
      console.log('- Last 50 chars:', token.substring(token.length - 50));
      
      // Check for problematic characters
      const hasNewlines = token.includes('\n');
      const hasCarriageReturns = token.includes('\r');
      const hasSpaces = token.includes(' ');
      const hasTabs = token.includes('\t');
      const hasProblematicChars = hasNewlines || hasCarriageReturns || hasSpaces || hasTabs;
      
      console.log('🔍 Character Analysis:');
      console.log('- Contains newlines:', hasNewlines);
      console.log('- Contains carriage returns:', hasCarriageReturns);
      console.log('- Contains spaces:', hasSpaces);
      console.log('- Contains tabs:', hasTabs);
      
      // JWT Structure Analysis
      const parts = token.split('.');
      console.log('🏗️ JWT Structure:');
      console.log('- JWT parts count:', parts.length, '(should be 3)');
      
      let header: any = null;
      let payload: any = null;
      let isExpired = true;
      let timeUntilExpiry = 0;
      
      if (parts.length === 3) {
        try {
          // Decode header
          header = JSON.parse(atob(parts[0]));
          console.log('📋 JWT Header:', header);
          
          // Decode payload
          payload = JSON.parse(atob(parts[1]));
          console.log('📦 JWT Payload:', payload);
          
          // Check expiry
          const now = Math.floor(Date.now() / 1000);
          const exp = payload.exp;
          const iat = payload.iat;
          
          isExpired = now > exp;
          timeUntilExpiry = Math.max(0, exp - now);
          
          console.log('⏰ Token Timing:');
          console.log('- Issued at:', new Date(iat * 1000));
          console.log('- Expires at:', new Date(exp * 1000));
          console.log('- Current time:', new Date());
          console.log('- Token expired:', isExpired);
          console.log('- Time until expiry:', timeUntilExpiry, 'seconds');
          
          // Check audience and issuer
          console.log('🎯 Token Claims:');
          console.log('- Audience (aud):', payload.aud);
          console.log('- Issuer (iss):', payload.iss);
          console.log('- Subject (sub):', payload.sub);
          console.log('- Token use:', payload.token_use);
          
        } catch (decodeError) {
          console.error('❌ Error decoding JWT:', decodeError);
          return {
            isValid: false,
            isExpired: true,
            error: `JWT decode error: ${decodeError}`,
            analysis: {
              tokenLength: token.length,
              hasProblematicChars,
              partsCount: parts.length
            }
          };
        }
      } else {
        console.error('❌ Invalid JWT structure - should have 3 parts separated by dots');
        return {
          isValid: false,
          isExpired: true,
          error: 'Invalid JWT structure - should have 3 parts separated by dots',
          analysis: {
            tokenLength: token.length,
            hasProblematicChars,
            partsCount: parts.length
          }
        };
      }
      
      // Test Authorization header construction
      const authHeader = `Bearer ${token}`;
      console.log('🔐 Authorization Header:');
      console.log('- Header length:', authHeader.length);
      console.log('- Header format:', authHeader.substring(0, 20) + '...');
      console.log('- Starts with "Bearer ":', authHeader.startsWith('Bearer '));
      
      return {
        token,
        isValid: parts.length === 3,
        isExpired,
        claims: payload,
        header,
        authHeader,
        analysis: {
          tokenLength: token.length,
          hasProblematicChars,
          partsCount: parts.length,
          timeUntilExpiry
        }
      };
      
    } catch (error) {
      console.error('❌ Debug error:', error);
      return {
        isValid: false,
        isExpired: true,
        error: `Debug error: ${error}`,
        analysis: { hasProblematicChars: false }
      };
    }
  }

  static async testAPIEndpoint(endpoint: string = '/api/penholder/assignments/current'): Promise<{
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
    analysis: string;
  }> {
    console.log('🧪 Testing API Endpoint:', endpoint);
    
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const headers = Object.fromEntries(response.headers.entries());
      const responseText = await response.text();
      
      console.log('📡 Response Status:', response.status, response.statusText);
      console.log('📋 Response Headers:', headers);
      console.log('📄 Response Body:', responseText);
      
      // Analyze the response
      let analysis = '';
      if (response.status === 403) {
        try {
          const errorData = JSON.parse(responseText);
          if (errorData.message?.includes('Invalid key=value pair')) {
            analysis = '🚨 INFRASTRUCTURE ISSUE: Malformed API Gateway response - Check CloudFormation stack status';
          } else if (errorData.message?.includes('Missing Authentication Token')) {
            analysis = '✅ CORRECT BEHAVIOR: Proper authentication error';
          } else {
            analysis = '❓ UNKNOWN 403 ERROR: ' + errorData.message;
          }
        } catch (e) {
          analysis = '📄 Non-JSON 403 response body';
        }
      } else if (response.status === 401) {
        analysis = '✅ CORRECT BEHAVIOR: Unauthorized (token invalid/expired)';
      } else if (response.status === 200) {
        analysis = '✅ SUCCESS: API call successful';
      } else {
        analysis = `❓ UNEXPECTED STATUS: ${response.status}`;
      }
      
      return {
        status: response.status,
        statusText: response.statusText,
        headers,
        body: responseText,
        analysis
      };
      
    } catch (error) {
      console.error('❌ API test error:', error);
      return {
        status: 0,
        statusText: 'Error',
        headers: {},
        body: `Error: ${error}`,
        analysis: '❌ API test failed with error'
      };
    }
  }

  static async quickDiagnostic(): Promise<{
    tokenAnalysis: JWTDebugResult;
    apiTest?: any;
    recommendation: string;
  }> {
    console.log('⚡ Quick JWT Diagnostic');
    console.log('======================');
    
    const tokenAnalysis = await this.analyzeToken();
    
    if (tokenAnalysis.error) {
      return {
        tokenAnalysis,
        recommendation: '❌ Authentication system error - User needs to sign in again'
      };
    } else if (!tokenAnalysis.isValid) {
      return {
        tokenAnalysis,
        recommendation: '❌ Invalid JWT token structure - Check Cognito configuration'
      };
    } else if (tokenAnalysis.isExpired) {
      return {
        tokenAnalysis,
        recommendation: '⚠️ JWT token is expired - Refresh token or sign in again'
      };
    } else {
      console.log('✅ JWT token appears valid');
      console.log('🧪 Testing API endpoint...');
      const apiTest = await this.testAPIEndpoint();
      
      let recommendation = '';
      if (apiTest.status === 403 && apiTest.body.includes('Invalid key=value pair')) {
        recommendation = '🚨 CRITICAL: API Gateway configuration issue - JWT token is valid but API Gateway cannot parse it';
      } else if (apiTest.status === 401) {
        recommendation = '✅ WORKING: Authentication flow is working correctly';
      } else if (apiTest.status === 200) {
        recommendation = '✅ SUCCESS: Complete authentication and API access working';
      } else {
        recommendation = `❓ INVESTIGATE: Unexpected API response status ${apiTest.status}`;
      }
      
      return {
        tokenAnalysis,
        apiTest,
        recommendation
      };
    }
  }
}
